import { apiClient, API_ENDPOINTS } from './api';
import type { AuthResponse, LoginCredentials, SignupData, WaitlistSignupData } from '../types/auth';

// Helper function to handle AA API errors
const handleAAApiError = (error: any): string => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};


class AuthService {
  // Login user - Updated to use magic link instead of password
  async login(credentials: LoginCredentials): Promise<AuthResponse> {

    try {
      // Send magic link instead of password authentication
      const response = await this.sendMagicLink(credentials.email);

      // Return a response indicating magic link was sent
      // User will authenticate via email link
      return {
        token: '',
        refreshToken: '',
        user: {
          email: credentials.email,
          name: '',
          role: 'user',
          firstName: '',
          lastName: '',
          fullName: ''
        },
        message: response.message
      } as any;
    } catch (error: any) {
      // Handle errors from magic link endpoint
      const errorMessage = error.response?.data?.message || error.message || 'Failed to send magic link';
      throw new Error(errorMessage);
    }
  }

  // Signup new user - Updated for AA API format with magic link support
  // Note: Backend will automatically send welcome email, no need to call it here
  async signup(data: SignupData): Promise<AuthResponse> {
    console.log('AuthService.signup called with:', data);


    // Transform frontend signup data to match AA API expectations
    const aaUserData = {
      email: data.email,
      name: data.fullName, // AA expects 'name' field, not 'fullName'
      role: 'user' // Default role for new users
      // Password removed - using magic link authentication
    };

    try {
      const response = await apiClient.post(API_ENDPOINTS.auth.signup, aaUserData);

      // Handle AA API response format
      const userData = response.data.data;

      // Email verification is now required before login
      // Backend sends verification email automatically
      // Return user data without tokens (user must verify email first)
      return {
        user: {
          id: userData.id,
          email: userData.email,
          name: userData.name,
          role: userData.role,
          firstName: userData.name?.split(' ')[0] || '',
          lastName: userData.name?.split(' ').slice(1).join(' ') || '',
          fullName: userData.name
        },
        token: '', // No token until email verified
        refreshToken: '' // No refresh token until email verified
      };
    } catch (error) {
      console.error('Signup API error:', error);
      throw error;
    }
  }

  // Waitlist signup - for users joining the waitlist without full registration
  async waitlistSignup(data: WaitlistSignupData): Promise<AuthResponse> {
    console.log('AuthService.waitlistSignup called with:', data);

    const payload = {
      email: data.email,
      name: data.fullName,
      user_type: data.userType,
      role: 'user',
    };

    try {
      const response = await apiClient.post(API_ENDPOINTS.auth.signup, payload);
      console.log('Waitlist signup response:', response.data);

      if (response.data.success) {
        return {
          user: response.data.data || null,  // Backend returns user data directly in .data, not .data.user
          accessToken: null,
          refreshToken: null,
        };
      }
      throw new Error(response.data.message || 'Waitlist signup failed');
    } catch (error: any) {
      console.error('Waitlist signup error:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to join waitlist');
    }
  }

  // Logout user
  async logout(): Promise<void> {
    
    try {
      // Call AA logout endpoint (JWT tokens are stateless, so this mainly logs the action)
      await apiClient.post(API_ENDPOINTS.auth.logout);
    } finally {
      // Clear all auth-related storage regardless of API response
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('tenant_id');
    }
  }

  // Get current user - Updated for AA API response format
  async getCurrentUser(): Promise<{ user: any }> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.auth.getCurrentUser);
      
      // Handle AA API response format - ApiResponse<User> structure
      // API returns { success: true, data: User, message: null }
      if (!response.data.success) {
        const error = new Error(response.data.message || 'Failed to get current user');
        error.name = 'GetUserError';
        throw error;
      }
      
      const userData = response.data.data;
      return { 
        user: {
          ...userData,
          // Ensure compatibility with frontend User interface
          firstName: userData.name?.split(' ')[0] || '',
          lastName: userData.name?.split(' ').slice(1).join(' ') || '',
          fullName: userData.name
        }
      };
    } catch (error: any) {
      // Handle 401 specifically to avoid unnecessary error logging
      if (error.response?.status === 401) {
        const authError = new Error('Authentication required');
        authError.name = 'AuthenticationError';
        throw authError;
      }
      
      // Re-throw other errors
      throw error;
    }
  }

  // Google OAuth login
  async googleLogin(googleToken: string): Promise<AuthResponse> {
    
    const response = await apiClient.post(API_ENDPOINTS.auth.googleAuth, { googleToken });
    const { token, refreshToken, user } = response.data;
    
    // Store tokens
    localStorage.setItem('auth_token', token);
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }
    
    return { token, refreshToken, user };
  }

  // Check if user is authenticated (client-side only)
  isAuthenticated(): boolean {
    return !!localStorage.getItem('auth_token');
  }

  // Get stored token
  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  // Token refresh functionality
  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      const error = new Error('No refresh token available');
      error.name = 'NoRefreshTokenError';
      throw error;
    }

    try {
      const response = await apiClient.post(API_ENDPOINTS.auth.refreshToken, {}, {
        headers: {
          'Authorization': `Bearer ${refreshToken}`
        }
      });

      if (!response.data.success) {
        const error = new Error(response.data.message || 'Token refresh failed');
        error.name = 'RefreshTokenError';
        throw error;
      }

      const authData = response.data.data;
      const newToken = authData.access_token;
      const newRefreshToken = authData.refresh_token || refreshToken; // Keep existing if not provided
      const user = authData.user;

      // Update stored tokens
      if (newToken) {
        localStorage.setItem('auth_token', newToken);
      }
      if (newRefreshToken) {
        localStorage.setItem('refresh_token', newRefreshToken);
      }

      return { 
        token: newToken, 
        refreshToken: newRefreshToken, 
        user: {
          ...user,
          firstName: user.name?.split(' ')[0] || '',
          lastName: user.name?.split(' ').slice(1).join(' ') || '',
          fullName: user.name
        }
      };
    } catch (error: any) {
      // If refresh fails, clear tokens but don't log errors as they're often expected
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('tenant_id');
      
      // Enhance error with more specific information
      if (error.response?.status === 401) {
        const refreshError = new Error('Refresh token expired or invalid');
        refreshError.name = 'RefreshTokenExpiredError';
        throw refreshError;
      }
      
      if (error.response?.status === 500) {
        const serverError = new Error('Server error during token refresh');
        serverError.name = 'RefreshServerError';
        throw serverError;
      }
      
      throw error;
    }
  }

  // Get stored tenant ID
  getTenantId(): string | null {
    return localStorage.getItem('tenant_id');
  }

  // Development helper: Test AA API connection
  async testAAConnection(): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      // Simple health check or test endpoint
      const response = await apiClient.get('/health');
      return {
        success: true,
        message: 'AA API connection successful',
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        message: `AA API connection failed: ${handleAAApiError(error)}`,
        data: error
      };
    }
  }

  // Verify email with token and auto-login
  async verifyEmail(token: string): Promise<AuthResponse & { waitlist?: boolean }> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.auth.verifyEmail, { token });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Email verification failed');
      }

      const authData = response.data.data;

      // Check if user is on waitlist (no tokens returned)
      if (authData.waitlist === true) {
        console.log('User is on waitlist:', authData);
        return {
          waitlist: true,
          user: authData.user ? {
            ...authData.user,
            firstName: authData.user.name?.split(' ')[0] || '',
            lastName: authData.user.name?.split(' ').slice(1).join(' ') || '',
            fullName: authData.user.name,
            waitlistStatus: authData.user.waitlist_status
          } : null,
          accessToken: null,
          refreshToken: null,
        };
      }

      // Approved user - extract tokens
      const accessToken = authData.access_token;
      const refreshToken = authData.refresh_token;
      const user = authData.user;

      // Store tokens for auto-login
      if (accessToken) {
        localStorage.setItem('auth_token', accessToken);
      }
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }
      if (user.id) {
        localStorage.setItem('tenant_id', user.id);
      }

      return {
        accessToken: accessToken,
        refreshToken: refreshToken,
        user: {
          ...user,
          firstName: user.name?.split(' ')[0] || '',
          lastName: user.name?.split(' ').slice(1).join(' ') || '',
          fullName: user.name
        }
      };
    } catch (error: any) {
      const errorMessage = handleAAApiError(error);
      throw new Error(errorMessage);
    }
  }

  // Send magic link for passwordless authentication
  async sendMagicLink(email: string): Promise<{ message: string; user_exists: boolean }> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.auth.sendMagicLink, { email });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to send magic link');
      }

      return {
        message: response.data.message || 'Magic link sent successfully. Please check your email.',
        user_exists: response.data.data?.user_exists || false
      };
    } catch (error: any) {
      const errorMessage = handleAAApiError(error);
      throw new Error(errorMessage);
    }
  }

  // Resend verification email
  async resendVerification(email: string): Promise<{ message: string }> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.auth.resendVerification, { email });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to resend verification email');
      }

      return {
        message: response.data.message || 'Verification email sent successfully'
      };
    } catch (error: any) {
      const errorMessage = handleAAApiError(error);
      throw new Error(errorMessage);
    }
  }

  // Development helper: Get AA API configuration
  getAAConfig(): { baseURL: string; endpoints: any; mockMode: boolean } {
    return {
      baseURL: apiClient.defaults.baseURL || 'Unknown',
      endpoints: API_ENDPOINTS.auth,
      mockMode: false
    };
  }
}

export const authService = new AuthService();
export { AuthService };
export default AuthService;